import React, { useContext, useState, useEffect, useRef } from 'react';
import {prepareRmAction, completeRmAction, readRmAction} from '../../utils/rmActionAttempt';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    useToast,
    Divider,
    Progress,
    Textarea,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Collapse,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Select,
} from '@chakra-ui/react';
import {
    FaPhone,
    FaUsers,
    FaSync,
    FaMapMarkerAlt,
    FaUser,
    FaLightbulb,
    FaChartLine,
    FaShieldAlt,
    FaListUl,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useSearchParams } from 'react-router-dom';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import { buildSyntheticWhoToCall, demoCustomers, getCustomerDisplayName, getQueueOwnerLabel } from '../../content/demoAnalytics';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import PilotComparison from '../../compnents/PilotComparison';
import QueueReadingGuide from '../../compnents/QueueReadingGuide';
import { downloadReviewDossier } from '../../utils/reviewDossier';
import ScoreFreshnessBadge from '../../compnents/ScoreFreshnessBadge';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function RMWhoToCallContent() {
    const { token, user } = useContext(AuthContext);
    const ownerKey = user?._id || user?.id || user?.email;
    const busyCustomers = useRef(new Set());
    const [outcomeNotes, setOutcomeNotes] = useState({});
    const [followUpDates, setFollowUpDates] = useState({});
    const [searchParams] = useSearchParams();
    const [callList, setCallList] = useState([]);
    const [actionMap, setActionMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [usingDemoData, setUsingDemoData] = useState(false);
    const [urgencyFilter, setUrgencyFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    // Optional per-customer due date applied to the next action created from
    // that card (yyyy-mm-dd from the date input).
    const [dueDates, setDueDates] = useState({});
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const [actionSubmitting, setActionSubmitting] = useState({});
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    const buildLatestActionMap = (actions = []) =>
        actions.reduce((acc, action) => {
            const existing = acc[action.customer_id];
            const candidateDate = new Date(action.updated_at || action.created_at || 0).getTime();
            const existingDate = existing ? new Date(existing.updated_at || existing.created_at || 0).getTime() : 0;

            if (!existing || candidateDate >= existingDate) {
                acc[action.customer_id] = action;
            }

            return acc;
        }, {});

    const fetchActionSummary = async ({ showToastOnError = false } = {}) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/content/rm-actions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const actions = Array.isArray(response.data) ? response.data : [];
            setActionMap(buildLatestActionMap(actions));
        } catch (err) {
            console.error('Error fetching RM actions:', err);
            if (showToastOnError) {
                toast({
                    title: 'Action tracking unavailable',
                    description: err.response?.data?.message || 'Failed to fetch RM action history.',
                    status: 'warning',
                    duration: 3500,
                    isClosable: true,
                    position: 'top',
                });
            }
        }
    };

    const fetchWhoToCall = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/content/rm-who-to-call`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const items = Array.isArray(response.data) ? response.data : [];
            // Live data is shown as-is (enrichment preserves backend-computed
            // fields). An empty book shows a real empty state — never a silent
            // synthetic substitution in a live tenant.
            setCallList(items.length > 0 ? buildSyntheticWhoToCall(items) : []);
            setUsingDemoData(false);
        } catch (err) {
            console.error('Error fetching who to call:', err);
            // Surface the failure honestly. We still render a clearly-labeled
            // synthetic sample so the workspace is not blank, but the error is
            // shown and the sample is banner-marked as demo data.
            setError({
                status: 'warning',
                title: 'Live queue unavailable',
                description: 'We could not load your scored book, so the list below is a synthetic sample. Refresh to try again.',
                emptyTitle: 'Live queue unavailable',
                emptyHint: 'Check your connection or try Refresh in a moment.',
            });
            setCallList(buildSyntheticWhoToCall(demoCustomers));
            setUsingDemoData(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchWhoToCall();
            fetchActionSummary();
        }
        // eslint-disable-next-line
    }, [token]);

    const createTrackedAction = async (customer, actionType, notes) => {
        const dueDate = dueDates[customer.customer_id];
        const payload = prepareRmAction({storage: window.localStorage, owner: ownerKey, customer: customer.customer_id,
            payload: {customer_id: customer.customer_id, action_type: actionType, notes,
                priority_score: customer.priority_score, reason: customer.recommended_action,
                ...(dueDate ? {due_date: new Date(`${dueDate}T17:00:00`).toISOString()} : {})}});
        const response = await axios.post(
            `${API_BASE_URL}/content/rm-actions`, payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (!response.data?.id || response.data.customer_id !== customer.customer_id) {
            throw new Error('The action was not confirmed. Retry the same action.');
        }
        setActionMap(previous => ({...previous, [customer.customer_id]: response.data}));
        return response.data;
    };

    const updateTrackedAction = async (actionId, payload) => {
        const response = await axios.put(
            `${API_BASE_URL}/content/rm-actions/${actionId}`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    };

    const snoozeTrackedAction = async (actionId, payload) => {
        const response = await axios.post(
            `${API_BASE_URL}/content/rm-actions/${actionId}/snooze`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    };

    const recordTrackedOutcome = async (actionId, payload) => {
        const response = await axios.post(
            `${API_BASE_URL}/content/rm-actions/${actionId}/outcome`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    };

    // Records the outcome of an action, creating one first if the customer has
    // not been actioned yet, so the "Who to call" loop always closes on a
    // documented result.
    const handleRecordOutcome = async (customer, outcome) => {
        if (busyCustomers.current.has(customer.customer_id)) return;
        busyCustomers.current.add(customer.customer_id);
        withActionSubmitting(customer.customer_id, true);
        try {
            let actionId = actionMap[customer.customer_id]?.id;
            const pending = readRmAction({storage: window.localStorage, owner: ownerKey, customer: customer.customer_id});
            if (pending) actionId = (await createTrackedAction(customer, pending.action_type, pending.notes)).id;
            if (!actionId) {
                const created = await createTrackedAction(
                    customer,
                    'call',
                    `Logged from who-to-call queue. Why: ${customer.recommended_action}`
                );
                actionId = created.id;
            }
            const followUp = followUpDates[customer.customer_id];
            await recordTrackedOutcome(actionId, {outcome, outcome_notes: outcomeNotes[customer.customer_id] || null,
                mark_done: !followUp, ...(followUp ? {next_contact_date: new Date(`${followUp}T17:00:00`).toISOString()} : {})});
            completeRmAction({storage: window.localStorage, owner: ownerKey, customer: customer.customer_id});
            await fetchActionSummary();
            toast({
                title: 'Outcome recorded',
                description: `${formatOutcome(outcome)} saved for ${customer.customer_id}.`,
                status: 'success',
                duration: 2500,
                isClosable: true,
                position: 'top',
            });
        } catch (err) {
            console.error('Error recording outcome:', err);
            toast({
                title: 'Could not record outcome',
                description: err.response?.data?.message || err.message || 'Failed to record the outcome.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            busyCustomers.current.delete(customer.customer_id);
            withActionSubmitting(customer.customer_id, false);
        }
    };

    const withActionSubmitting = (customerId, isSubmitting) => {
        setActionSubmitting((prev) => ({
            ...prev,
            [customerId]: isSubmitting,
        }));
    };

    const handleTrackedAction = async (customer, intent) => {
        if (busyCustomers.current.has(customer.customer_id)) return;
        busyCustomers.current.add(customer.customer_id);
        let existingAction = actionMap[customer.customer_id];
        withActionSubmitting(customer.customer_id, true);

        try {
            const pending = readRmAction({storage: window.localStorage, owner: ownerKey, customer: customer.customer_id});
            if (pending && ['complete', 'snooze'].includes(intent)) existingAction = await createTrackedAction(customer, pending.action_type, pending.notes);
            if (intent === 'contacted') {
                await createTrackedAction(
                    customer,
                    'call',
                    `Contacted from who-to-call queue. Recommended action: ${customer.recommended_action}`
                );
            } else if (intent === 'meeting') {
                await createTrackedAction(
                    customer,
                    'meeting',
                    `Meeting requested from who-to-call queue. Recommended action: ${customer.recommended_action}`
                );
            } else if (intent === 'complete') {
                let actionId = existingAction?.id;

                if (!actionId) {
                    const created = await createTrackedAction(
                        customer,
                        'note',
                        `Completed directly from who-to-call queue. Recommended action: ${customer.recommended_action}`
                    );
                    actionId = created.id;
                }

                await updateTrackedAction(actionId, {
                    status: 'done',
                    notes: `Completed from who-to-call queue. Recommended action: ${customer.recommended_action}`,
                });
            } else if (intent === 'snooze') {
                let actionId = existingAction?.id;

                if (!actionId) {
                    const created = await createTrackedAction(
                        customer,
                        'note',
                        `Deferred from who-to-call queue. Recommended action: ${customer.recommended_action}`
                    );
                    actionId = created.id;
                }

                await snoozeTrackedAction(actionId, {
                    snooze_days: 7,
                    notes: 'Deferred for one week from the who-to-call workflow.',
                });
            }

            completeRmAction({storage: window.localStorage, owner: ownerKey, customer: customer.customer_id});
            await fetchActionSummary();

            toast({
                title: 'Action saved',
                description: `Tracking updated for ${customer.customer_id}.`,
                status: 'success',
                duration: 2500,
                isClosable: true,
                position: 'top',
            });
        } catch (err) {
            console.error('Error updating RM action tracking:', err);
            toast({
                title: 'Could not save action',
                description: err.response?.data?.message || err.message || 'Failed to update the who-to-call workflow.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            busyCustomers.current.delete(customer.customer_id);
            withActionSubmitting(customer.customer_id, false);
        }
    };

    const formatEvidenceCoverage = (coverage) => {
        if (coverage === null || coverage === undefined || Number.isNaN(Number(coverage))) {
            return 'Evidence n/a';
        }
        return `Evidence ${Math.round(Number(coverage) * 100)}%`;
    };

    const formatFreshness = (asOf) => {
        if (!asOf) return 'unknown';
        const then = new Date(asOf).getTime();
        if (Number.isNaN(then)) return 'unknown';
        const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
        if (days <= 0) return 'today';
        if (days === 1) return 'yesterday';
        if (days < 30) return `${days}d ago`;
        return `${Math.floor(days / 30)}mo ago`;
    };

    const formatOutcome = (outcome) => {
        if (!outcome) return '';
        return outcome.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getUrgencyColor = (urgencyLevel) => {
        const level = urgencyLevel?.toLowerCase();
        if (level === 'urgent' || level === 'high') return 'red';
        if (level === 'medium' || level === 'moderate') return 'orange';
        return 'green';
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return 'red';
        if (score >= 6) return 'orange';
        if (score >= 4) return 'yellow';
        return 'green';
    };

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getRiskLabel = (riskProfile) => {
        if (riskProfile <= 3) return 'Low';
        if (riskProfile <= 6) return 'Medium';
        if (riskProfile <= 8) return 'High';
        return 'Very High';
    };

    const getActionStatusColor = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'done':
                return 'green';
            case 'snoozed':
                return 'orange';
            case 'dismissed':
                return 'gray';
            default:
                return 'blue';
        }
    };

    const formatActionType = (actionType) => {
        switch ((actionType || '').toLowerCase()) {
            case 'call':
                return 'Call logged';
            case 'meeting':
                return 'Meeting planned';
            case 'email':
                return 'Email sent';
            case 'note':
                return 'Workflow note';
            default:
                return actionType || 'No action yet';
        }
    };

    const formatActionStatus = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'done':
                return 'Completed';
            case 'snoozed':
                return 'Snoozed';
            case 'dismissed':
                return 'Dismissed';
            default:
                return 'Open';
        }
    };

    const formatDateLabel = (dateValue) => {
        if (!dateValue) return 'Not scheduled';

        const parsedDate = new Date(dateValue);
        if (Number.isNaN(parsedDate.getTime())) return 'Not scheduled';

        return parsedDate.toLocaleDateString();
    };

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredCallList already
    };

    // ---- Stats ----
    const avgPriority =
        callList.length > 0
            ? (callList.reduce((sum, c) => sum + c.priority_score, 0) / callList.length).toFixed(2)
            : 0;
    const highUrgencyCount = callList.filter(
        (c) => c.urgency_level?.toLowerCase() === 'high' || c.urgency_level?.toLowerCase() === 'urgent'
    ).length;
    const trackedCustomersCount = Object.keys(actionMap).length;

    const matchesUrgency = (customer) => {
        if (urgencyFilter === 'all') return true;
        return String(customer.urgency_level || '').toLowerCase() === urgencyFilter;
    };

    const matchesWorkflowStatus = (customer) => {
        if (statusFilter === 'all') return true;
        const latest = actionMap[customer.customer_id];
        const status = (latest?.status || 'none').toLowerCase();
        if (statusFilter === 'untouched') return !latest;
        if (statusFilter === 'open') return status === 'open' || status === 'snoozed';
        if (statusFilter === 'done') return status === 'done';
        return true;
    };

    const filteredCallList = callList.filter(customer =>
        (String(customer.customer_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(customer.country_iso || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(getCustomerDisplayName(customer) || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(customer.segment || '').toLowerCase().includes(searchTerm.toLowerCase())) &&
        matchesUrgency(customer) &&
        matchesWorkflowStatus(customer)
    );

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaListUl} boxSize={{ base: 6, md: 8 }} />
                                <Text>Who To Call</Text>
                            </HStack>
                        </Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            Customers ranked by the FinanSee priority engine
                        </Text>
                    </Box>

                    {!loading && (usingDemoData || (callList.length > 0 && callList.every((item) => item.is_synthetic))) ? (
                        <SyntheticPreviewBanner message={usingDemoData
                            ? 'Demo / synthetic sample — the live scored queue could not be loaded. These are not your real customers.'
                            : 'No scored customers are available for this account yet, so this queue is a synthetic sample. Upload a customer dataset to rank your own book.'} />
                    ) : null}

                    {/* Advisers compare on their own book; a Solo admin (who is the adviser) on the whole book. */}
                    {(user?.role === 'rm' || (user?.role === 'admin' && user?.subscription === 'solo'))
                        && <PilotComparison key={ownerKey} ownerKey={ownerKey} token={token} apiBase={API_BASE_URL} />}
                    {/* Statistics Cards */}
                    {!loading && (
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                            <Card bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaUsers} color="brand.tealDark" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                Total Customers
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="brand.navy">
                                            {callList.length}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaChartLine} color="brand.teal" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                Avg Priority
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="brand.navy">
                                            {avgPriority}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaShieldAlt} color="orange.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                High (top of book)
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="orange.600">
                                            {highUrgencyCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaPhone} color="brand.tealDark" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                Tracked
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="brand.navy">
                                            {trackedCustomersCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    )}

                    {!usingDemoData && callList.length > 0 && !callList.every((item) => item.is_synthetic) && <QueueReadingGuide items={callList} />}

                    {/* Error Alert */}
                    {error && (
                        <Alert status={error.status} borderRadius="xl" variant="left-accent" boxShadow="sm">
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }}>{error.title}</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>{error.description}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search, filters, and Refresh */}
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Flex gap={3} flexWrap="wrap" align="center">
                                <InputGroup flex={1} minW="220px">
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor="gray.300"
                                        _focus={{ borderColor: 'brand.teal', boxShadow: '0 0 0 1px var(--chakra-colors-brand-teal)' }}
                                    />
                                </InputGroup>
                                <Select
                                    aria-label="Filter by urgency"
                                    value={urgencyFilter}
                                    onChange={(e) => setUrgencyFilter(e.target.value)}
                                    maxW="170px"
                                    borderColor="gray.300"
                                >
                                    <option value="all">All urgencies</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </Select>
                                <Select
                                    aria-label="Filter by workflow status"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    maxW="190px"
                                    borderColor="gray.300"
                                >
                                    <option value="all">All statuses</option>
                                    <option value="untouched">No action yet</option>
                                    <option value="open">Open / snoozed</option>
                                    <option value="done">Completed</option>
                                </Select>
                                <Button
                                    leftIcon={<FaSync />}
                                    colorScheme="teal"
                                    onClick={fetchWhoToCall}
                                    isLoading={loading}
                                    _hover={{ bg: 'brand.navy' }}
                                >
                                    Refresh
                                </Button>
                            </Flex>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color="brand.tealDark" thickness="4px" />
                                <Text color="gray.500" fontSize="sm">Loading recommendations...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredCallList.length === 0 ? (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <EmptyStateCard
                                    icon={searchTerm || urgencyFilter !== 'all' || statusFilter !== 'all' ? FaSearch : FaPhone}
                                    title={
                                        searchTerm || urgencyFilter !== 'all' || statusFilter !== 'all'
                                            ? 'No customers match these filters'
                                            : error?.emptyTitle || 'No call recommendations right now'
                                    }
                                    description={
                                        searchTerm
                                            ? `We could not find any customers matching "${searchTerm}".`
                                            : (urgencyFilter !== 'all' || statusFilter !== 'all')
                                                ? 'No customers in the queue match the selected urgency and workflow status.'
                                                : error?.description || 'As soon as prioritized outreach opportunities are available, they will appear in this queue.'
                                    }
                                    hint={
                                        searchTerm || urgencyFilter !== 'all' || statusFilter !== 'all'
                                            ? 'Broaden or clear the filters to return to the full recommendation list.'
                                            : error?.emptyHint || 'Refresh after new scoring runs, customer updates, or RM assignments.'
                                    }
                                    borderColor="brand.divider"
                                    titleColor="gray.700"
                                    descriptionColor="gray.600"
                                    hintColor="gray.500"
                                    iconColor="brand.tealDark"
                                    maxW="full"
                                >
                                    {searchTerm ? (
                                        <Button
                                            variant="outline"
                                            borderColor="brand.teal"
                                            color="brand.tealDark"
                                            _hover={{ bg: 'brand.tealSoft' }}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    ) : (
                                        <Button colorScheme="teal" onClick={fetchWhoToCall}>
                                            Refresh recommendations
                                        </Button>
                                    )}
                                </EmptyStateCard>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredCallList.map((customer, index) => (
                                (() => {
                                    const latestAction = actionMap[customer.customer_id];
                                    const isSubmittingAction = Boolean(actionSubmitting[customer.customer_id]);

                                    return (
                                <Card
                                    key={customer.customer_id || index}
                                    bg="white"
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Heading size="sm" color="brand.navy">
                                                    {getCustomerDisplayName(customer)}
                                                </Heading>
                                                <Text fontSize="xs" color="gray.500" fontFamily="mono">
                                                    {customer.customer_id}
                                                </Text>
                                                <HStack spacing={4} flexWrap="wrap">
                                                    <Badge
                                                        colorScheme={getUrgencyColor(customer.urgency_level)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {customer.urgency_level}
                                                    </Badge>
                                                    <ScoreFreshnessBadge freshness={customer.freshness} />
                                                    {Number(customer.history_snapshots) === 1 ? (
                                                        <Badge variant="outline" colorScheme="gray" fontSize="2xs" title="Only one dated snapshot: change over time is not measured yet">
                                                            First snapshot
                                                        </Badge>
                                                    ) : null}
                                                    <HStack spacing={1}>
                                                        <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3.5} />
                                                        <Text fontSize="sm" color="gray.600">
                                                            {customer.country_iso}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Icon as={FaUser} color="gray.400" boxSize={3.5} />
                                                        <Text fontSize="sm" color="gray.600">
                                                            {customer.age} years
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                                            Priority:
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            color={customer.actionable === false ? 'gray.500' : `${getPriorityColor(customer.priority_score)}.600`}
                                                            fontWeight="bold"
                                                            opacity={customer.actionable === false ? 0.55 : 1}
                                                            textDecoration={customer.actionable === false ? 'line-through' : 'none'}
                                                        >
                                                            {customer.priority_score}/10
                                                        </Text>
                                                    </HStack>
                                                </HStack>
                                                {/* Compact operational metadata: reason, evidence coverage, freshness, owner, status */}
                                                <Text fontSize="sm" color="gray.700" noOfLines={1} title={customer.recommended_action}>
                                                    <Text as="span" color="gray.500" fontWeight="medium">Why: </Text>
                                                    {customer.recommended_action || 'Reason unavailable'}
                                                </Text>
                                                <HStack spacing={3} flexWrap="wrap" fontSize="xs" color="gray.500">
                                                    <Text>{formatEvidenceCoverage(customer.evidence_coverage)}</Text>
                                                    <Text>·</Text>
                                                    <Text>Data observed {formatFreshness(customer.source_observed_at || customer.as_of)}</Text>
                                                    <Text>·</Text>
                                                    <Text>Owner {getQueueOwnerLabel(customer, user, usingDemoData)}</Text>
                                                    <Text>·</Text>
                                                    <Badge
                                                        colorScheme={getActionStatusColor(latestAction?.status)}
                                                        variant="subtle"
                                                        fontSize="10px"
                                                    >
                                                        {latestAction ? formatActionStatus(latestAction.status) : 'No action yet'}
                                                    </Badge>
                                                    {latestAction?.outcome ? (
                                                        <Badge colorScheme="purple" variant="subtle" fontSize="10px">
                                                            Outcome: {formatOutcome(latestAction.outcome)}
                                                        </Badge>
                                                    ) : null}
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="teal"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.200">
                                                <VStack align="stretch" spacing={4}>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaUser} color="brand.tealDark" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Customer Details</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Customer ID:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.customer_id}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Age:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.age} years
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Country:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.country_iso}
                                                                        </Text>
                                                                    </HStack>
                                                                    <Divider />
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Risk Profile:</Text>
                                                                        <Badge
                                                                            colorScheme={getRiskColor(customer.risk_profile)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {getRiskLabel(customer.risk_profile)} ({customer.risk_profile}/10)
                                                                        </Badge>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>

                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaChartLine} color="brand.tealDark" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Priority Information</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={3}>
                                                                    <Box>
                                                                        <HStack justify="space-between" mb={1}>
                                                                            <Text fontSize="sm" color="gray.600">Priority Score:</Text>
                                                                            <Text
                                                                                fontSize="sm"
                                                                                color={customer.actionable === false ? 'gray.500' : `${getPriorityColor(customer.priority_score)}.600`}
                                                                                fontWeight="bold"
                                                                                opacity={customer.actionable === false ? 0.55 : 1}
                                                                                textDecoration={customer.actionable === false ? 'line-through' : 'none'}
                                                                            >
                                                                                {customer.priority_score}/10
                                                                            </Text>
                                                                        </HStack>
                                                                        <Progress
                                                                            value={customer.priority_score * 10}
                                                                            size="sm"
                                                                            colorScheme={getPriorityColor(customer.priority_score)}
                                                                            borderRadius="full"
                                                                            opacity={customer.actionable === false ? 0.35 : 1}
                                                                        />
                                                                    </Box>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Evidence freshness:</Text>
                                                                        <ScoreFreshnessBadge freshness={customer.freshness} />
                                                                    </HStack>
                                                                    <Text fontSize="xs" color="gray.600">
                                                                        Imported {formatFreshness(customer.imported_at)} · Calculated {formatFreshness(customer.computed_at)}
                                                                    </Text>
                                                                    <Text fontSize="xs" color="gray.600">
                                                                        Observed priority inputs: {Object.entries(customer.breakdown?.observed_factors || {}).filter(([, observed]) => observed).map(([field]) => field.replaceAll('_', ' ')).join(', ') || 'none'}.
                                                                        {' '}Trading inactivity does not establish a contact deadline.
                                                                    </Text>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Urgency Level:</Text>
                                                                        <Badge
                                                                            colorScheme={getUrgencyColor(customer.urgency_level)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {customer.urgency_level}
                                                                        </Badge>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>

                                                    {/* Recommended Action */}
                                                    <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                        <CardBody>
                                                            <HStack spacing={2} mb={2}>
                                                                <Icon as={FaLightbulb} color="brand.teal" boxSize={5} />
                                                                <Text fontWeight="bold" color="gray.700">Recommended Action</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                                                                {customer.recommended_action}
                                                            </Text>
                                                        </CardBody>
                                                    </Card>

                                                    <Card bg="white" borderRadius="md" boxShadow="sm" border="1px" borderColor="gray.200">
                                                        <CardBody>
                                                            <VStack align="stretch" spacing={4}>
                                                                <HStack justify="space-between" align="start" flexWrap="wrap">
                                                                    <Box>
                                                                        <Text fontWeight="bold" color="gray.700">
                                                                            Action Tracking
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.600" mt={1}>
                                                                            Turn this recommendation into a visible RM workflow step.
                                                                        </Text>
                                                                    </Box>
                                                                    {latestAction ? (
                                                                        <Badge colorScheme={getActionStatusColor(latestAction.status)} px={3} py={1} borderRadius="full">
                                                                            {formatActionStatus(latestAction.status)}
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge colorScheme="gray" px={3} py={1} borderRadius="full">
                                                                            Not tracked yet
                                                                        </Badge>
                                                                    )}
                                                                </HStack>

                                                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                                                    <Box>
                                                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
                                                                            Latest step
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mt={1}>
                                                                            {formatActionType(latestAction?.action_type)}
                                                                        </Text>
                                                                    </Box>
                                                                    <Box>
                                                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
                                                                            Updated
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mt={1}>
                                                                            {formatDateLabel(latestAction?.updated_at || latestAction?.created_at)}
                                                                        </Text>
                                                                    </Box>
                                                                    <Box>
                                                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
                                                                            Next contact
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mt={1}>
                                                                            {formatDateLabel(latestAction?.next_contact_date)}
                                                                        </Text>
                                                                    </Box>
                                                                </SimpleGrid>

                                                                {latestAction?.notes ? (
                                                                    <Box bg="brand.tealSoft" borderRadius="md" px={3} py={3}>
                                                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
                                                                            Latest note
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.700" mt={1} whiteSpace="pre-line">
                                                                            {latestAction.notes}
                                                                        </Text>
                                                                    </Box>
                                                                ) : null}

                                                                <HStack spacing={2} align="center">
                                                                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                                                        Due date (optional):
                                                                    </Text>
                                                                    <Input
                                                                        type="date"
                                                                        size="sm"
                                                                        maxW="180px"
                                                                        aria-label={`Due date for ${customer.customer_id}`}
                                                                        value={dueDates[customer.customer_id] || ''}
                                                                        onChange={(e) => setDueDates((prev) => ({
                                                                            ...prev,
                                                                            [customer.customer_id]: e.target.value,
                                                                        }))}
                                                                    />
                                                                </HStack>

                                                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        borderColor="brand.teal"
                                                                        color="brand.tealDark"
                                                                        _hover={{ bg: 'brand.tealSoft' }}
                                                                        onClick={() => handleTrackedAction(customer, 'contacted')}
                                                                        isLoading={isSubmittingAction}
                                                                    >
                                                                        Contacted
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        borderColor="brand.teal"
                                                                        color="brand.tealDark"
                                                                        _hover={{ bg: 'brand.tealSoft' }}
                                                                        onClick={() => handleTrackedAction(customer, 'meeting')}
                                                                        isLoading={isSubmittingAction}
                                                                    >
                                                                        Appointment
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        colorScheme="green"
                                                                        onClick={() => handleTrackedAction(customer, 'complete')}
                                                                        isLoading={isSubmittingAction}
                                                                    >
                                                                        Complete
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        colorScheme="orange"
                                                                        variant="outline"
                                                                        onClick={() => handleTrackedAction(customer, 'snooze')}
                                                                        isLoading={isSubmittingAction}
                                                                    >
                                                                        Snooze 7d
                                                                    </Button>
                                                                </SimpleGrid>

                                                                <Box>
                                                                    <Text as="label" htmlFor={`outcome-notes-${customer.customer_id}`} fontSize="sm">Outcome notes</Text>
                                                                    <Textarea id={`outcome-notes-${customer.customer_id}`} value={outcomeNotes[customer.customer_id] || ''}
                                                                        onChange={event => setOutcomeNotes(previous => ({...previous, [customer.customer_id]: event.target.value}))}
                                                                        placeholder="What happened and what should happen next?" isDisabled={isSubmittingAction} />
                                                                    <Text as="label" htmlFor={`follow-up-${customer.customer_id}`} fontSize="sm">Next contact (optional)</Text>
                                                                    <Input id={`follow-up-${customer.customer_id}`} type="date" value={followUpDates[customer.customer_id] || ''}
                                                                        onChange={event => setFollowUpDates(previous => ({...previous, [customer.customer_id]: event.target.value}))} isDisabled={isSubmittingAction} />
                                                                    <Text fontSize="xs">Choosing a next contact keeps the action open after recording its outcome.</Text>
                                                                </Box>
                                                                {latestAction?.outcome_notes && <Text fontSize="sm">Last outcome notes: {latestAction.outcome_notes}</Text>}
                                                                {!usingDemoData && !customer.is_synthetic && (
                                                                    <Button size="sm" variant="outline" colorScheme="teal" width="full"
                                                                        onClick={async () => {
                                                                            try { await downloadReviewDossier(customer.customer_id, token); }
                                                                            catch (_) { toast({ title: 'Dossier unavailable', description: 'Please try again in a moment.', status: 'error', duration: 4000, isClosable: true }); }
                                                                        }}>
                                                                        Review dossier (PDF)
                                                                    </Button>
                                                                )}
                                                                <Menu>
                                                                    <MenuButton
                                                                        as={Button}
                                                                        size="sm"
                                                                        variant="outline"
                                                                        borderColor="purple.300"
                                                                        color="purple.600"
                                                                        _hover={{ bg: 'purple.50' }}
                                                                        rightIcon={<ChevronDownIcon />}
                                                                        isDisabled={isSubmittingAction}
                                                                        width="full"
                                                                    >
                                                                        Record outcome
                                                                    </MenuButton>
                                                                    <MenuList>
                                                                        {['contacted', 'scheduled', 'converted', 'no_answer', 'declined', 'resolved'].map((oc) => (
                                                                            <MenuItem key={oc} onClick={() => handleRecordOutcome(customer, oc)}>
                                                                                {formatOutcome(oc)}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </MenuList>
                                                                </Menu>
                                                            </VStack>
                                                        </CardBody>
                                                    </Card>
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                                    );
                                })()
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredCallList.length > 0 && (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <Text fontSize="sm" color="gray.600" textAlign="center">
                                    Showing {filteredCallList.length} of {callList.length} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default function DashboardRMwhoToCallPaid() {
    const {user} = useContext(AuthContext);
    return <RMWhoToCallContent key={user?._id || user?.id || user?.email || 'signed-out'} />;
}
